/* istanbul ignore file */
import React, { FC, useCallback, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import {
  Route,
  useLocation,
  useNavigate,
  Outlet,
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
} from 'react-router-dom'
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js'
import { Security } from '@okta/okta-react'
import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  InMemoryCache,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { StudioPage } from './screens/StudioPage'
import { ErrorScreen } from './components/ErrorScreen'
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary'
import {
  GRAPHQL_GATEWAY_URL,
  OKTA_CLIENT_ID,
  OKTA_ISSUER,
  OKTA_REDIRECT_URI,
} from './constants'
import { RendererProviderDefault } from '@moonpig/renderer-react-provider-default'
import { getQueryStringToggles } from './contexts/FeatureToggleProvider'
import CustomLoginCallback from './components/CustomLoginCallback'
import { encodePathArray } from './utils/path'
import { useOktaAuth } from '@okta/okta-react'

const oktaAuth = new OktaAuth({
  issuer: OKTA_ISSUER as string,
  clientId: OKTA_CLIENT_ID,
  redirectUri: OKTA_REDIRECT_URI,
})

if (
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_ENABLE_XSTATE_DEBUGGING === 'true'
) {
  require('@xstate/inspect').inspect({
    iframe: false,
  })
}

const authLink = setContext((_, { headers }) => {
  const token = oktaAuth.getAccessToken()
  return {
    headers: {
      ...headers,
      Authorization: `Bearer ${token}`,
    },
  }
})

const httpLink = createHttpLink({
  uri: GRAPHQL_GATEWAY_URL,
})

const client = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache(),
  connectToDevTools: true,
})

type TemplatePathStructure = {
  templateId: string
  path: string
}

const getPathStructure = (
  pathname: string,
  hash: string,
): TemplatePathStructure => {
  const pathArray = `${pathname}${decodeURI(hash)}`.replace('/', '').split('/')
  let templateId = ''
  let logicalPath = ''
  if (pathArray.length > 0) {
    if (
      /^[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}$/.test(
        pathArray[pathArray.length - 1]!,
      )
    ) {
      templateId = pathArray.pop() || ''
    } else {
      templateId = ''
    }
    logicalPath = encodePathArray(pathArray)
  }
  return {
    templateId,
    path: logicalPath,
  }
}

const StudioPageProvider: FC = () => {
  const location = useLocation()

  const toggles = getQueryStringToggles(location.search)

  const templatePathStructure = getPathStructure(
    location.pathname,
    location.hash,
  )

  return (
    <ApolloProvider client={client}>
      <RendererProviderDefault>
        <StudioPage
          toggles={toggles}
          templateId={templatePathStructure.templateId}
          logicalPath={templatePathStructure.path}
        />
      </RendererProviderDefault>
    </ApolloProvider>
  )
}

const NotFound: FC = () => {
  return (
    <ErrorScreen
      title={'Not found'}
      description={'The requested group or template is not found :('}
    />
  )
}

const RequiresAuthentication: React.FC = () => {
  const { oktaAuth, authState } = useOktaAuth()

  useEffect(() => {
    if (!authState) {
      return
    }

    if (!authState?.isAuthenticated) {
      const originalUri = toRelativeUrl(
        window.location.href,
        window.location.origin,
      )
      oktaAuth.setOriginalUri(originalUri)
      oktaAuth.signInWithRedirect()
    }
  }, [oktaAuth, authState?.isAuthenticated, authState])

  if (!authState || !authState?.isAuthenticated) {
    return (
      <div
        style={{
          margin: '-8px',
          backgroundColor: '#f0f1f3',
          display: 'flex',
          width: '100vw',
          height: '100vh',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 400 1080 300"
            style={{ width: '600px' }}
            preserveAspectRatio="xMidYMid meet"
          >
            <path
              vector-effect="non-scaling-stroke"
              d="m685.328 574.182-.439 4.955c-1.91 21.649-14.988 28.094-29.254 26.86-9.447-.818-17.662-5.044-22.69-17.862l-19.51 5.331c6.375 18.669 19.89 30.562 41.006 32.39 25.751 2.233 48.331-12.449 51.653-50.06l7.016-79.436-20.748-1.802-.812 9.175c-6.099-6.999-14.604-12.172-25.534-13.119-25.749-2.232-48.45 15.952-50.704 41.453-2.238 25.318 16.528 47.465 41.537 49.63 11.115.964 19.34-1.28 28.479-7.515m3.163-35.774c-1.3 14.676-13.219 24.92-28.777 23.572-15.38-1.332-24.767-13.423-23.468-28.097 1.297-14.68 12.643-24.786 28.022-23.457 15.559 1.35 25.52 13.305 24.223 27.982m-213.925 79.745h21.014v-48.805c6.693 7.365 16.178 10.68 27.893 10.68 24.549 0 45.375-20.442 45.375-45.855 0-25.598-21.012-45.67-46.862-45.67-10.228 0-19.339 4.048-26.594 10.865v-9.211h-20.826zm20.823-83.977c0-14.733 10.976-25.784 26.596-25.784 15.436 0 25.849 11.051 25.849 25.784 0 14.733-10.413 25.964-25.848 25.964-15.621 0-26.597-11.23-26.597-25.964M263.581 550.41c0-25.23-20.823-45.67-46.86-45.67-26.033 0-46.676 20.44-46.676 45.67 0 25.233 20.828 45.857 46.677 45.857 26.036 0 46.86-20.627 46.86-45.857m-21.383 0c0 13.996-10.787 25.97-25.475 25.97-14.321 0-25.294-11.79-25.294-25.787 0-13.81 10.787-25.966 25.292-25.966 14.507 0 25.477 11.787 25.477 25.783m339.424 35.368h21.011V498.3h-21.011zm-3.164-118.785c0-7.365 5.95-13.073 13.762-13.073 7.995 0 13.76 5.708 13.76 13.073 0 7.551-5.765 13.262-13.76 13.262-7.811 0-13.762-5.71-13.762-13.262m-140.18 81.674c1.395-15.777-5.263-22.824-17.489-23.882-11.303-.981-20.293 3.602-21.8 20.665l-4.118 46.599-20.933-1.816 7.698-87.142 20.934 1.813-.697 7.89c6.029-4.843 14.182-8.385 24.929-7.455 19.45 1.687 35.117 14.505 32.866 40.006l-4.586 51.917-20.937-1.813zm-72.322-14.534c0-25.232-20.828-45.67-46.865-45.67-26.034 0-46.677 20.44-46.677 45.67 0 25.232 20.829 45.857 46.677 45.857 26.037 0 46.865-20.627 46.865-45.857m-21.388 0c0 13.996-10.784 25.967-25.477 25.967-14.319 0-25.291-11.788-25.291-25.784 0-13.81 10.787-25.964 25.291-25.964 14.505 0 25.477 11.782 25.477 25.781M135.1 531.17c-1.474-16.692-8.258-21.652-20.113-20.624-10.932.946-19.339 7.222-17.961 22.816l4.231 47.882-20.934 1.813-4.134-46.779c-1.506-17.063-8.63-21.622-20.116-20.63-10.93.947-19.32 7.406-17.878 23.734l4.148 46.968-20.933 1.813-7.699-87.145 20.936-1.814.697 7.89c8.29-7.559 13.423-10.776 23.61-11.658 12.226-1.06 22.386 1.942 29.964 11.637 7.411-11.177 18.946-15.874 29.321-16.772 20.377-1.768 35.123 8.785 37.536 36.124l4.393 49.716-20.934 1.813z"
              fill="#ffa6b6"
              fill-rule="evenodd"
            />
            <path
              vector-effect="non-scaling-stroke"
              d="M1008.483 491.11H812.96c-35.148 0-63.64 28.904-63.64 64.56 0 35.656 28.492 64.56 63.64 64.56h195.523c35.145 0 63.637-28.904 63.637-64.56 0-35.656-28.492-64.56-63.637-64.56"
              fill="#e6eefa"
            />
            <text
              font-family="Moonpig"
              font-size="80"
              font-weight="700"
              fill="#00204c"
              transform="translate(910.11 554.87)"
            >
              <tspan x="-110.48" y="25.13">
                Studio
              </tspan>
            </text>
          </svg>
          <h3
            style={{
              marginTop: '10px',
              color: '#00204d',
              fontFamily: 'Moonpig',
            }}
          >
            Loading ..
          </h3>
        </div>
      </div>
    )
  }

  return <Outlet />
}

const Root: FC = () => {
  const navigate = useNavigate()

  const restoreOriginalUri = useCallback(
    async (_: any, originalUri: string) => {
      navigate(toRelativeUrl(originalUri || '/', window.location.origin), {
        replace: true,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <Outlet />
    </Security>
  )
}

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<Root />}>
      <Route element={<RequiresAuthentication />}>
        <Route path="/not-found" element={<NotFound />} />
        <Route path="/*" element={<StudioPageProvider />} />
      </Route>

      <Route path="/login/callback" element={<CustomLoginCallback />} />
    </Route>,
  ),
)

const container = document.getElementById('root')
const root = createRoot(container!)
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  </React.StrictMode>,
)
